/**
 * Monta o alerta de "Sincronizar status" a partir da resposta de
 * `GET /end-customer/instances/realtime-status`.
 *
 * Função pura pelo mesmo motivo de `montarAlertaDeCriacao`: o que ela decide é
 * conteúdo. E nasceu de um contrato quebrado — o `onSuccess` lia
 * `data.instances_updated`, campo que o backend nunca enviou, então o modal dizia
 * "Todos os status já estavam corretos!" em toda sincronização, inclusive quando
 * VMs mudaram de estado. Desde a #767 o backend responde três desfechos por VM, e
 * cada um chega à tela:
 *
 * - `updates` — o status mudou no banco; uma entrada com `reason: 'vm_not_found'`
 *   é a VM que o Proxmox não tem mais (marcada com erro pela mesma régua da
 *   reconciliação);
 * - `unchecked` — a VM não pôde ser verificada agora ("não sei": agent fora,
 *   listagem indisponível, VM em transição). Não é "estava correto".
 */

export interface MudancaDeStatus {
  instance_id: string
  instance_name: string
  old_status: string
  new_status: string
  proxmox_vm_id?: number | null
  reason?: string
}

export interface VmNaoConferida {
  instance_id: string
  instance_name: string
  proxmox_vm_id?: number | null
  reason: string
}

export interface RespostaDeSincronizacaoDeStatus {
  instances_checked: number
  updates?: MudancaDeStatus[]
  unchecked?: VmNaoConferida[]
}

export interface AlertaDeSincronizacao {
  title: string
  message: string
  variant: 'success' | 'info' | 'warning'
}

const MOTIVO_NAO_ENCONTRADA = 'vm_not_found'
// A VM foi verificada, mas o status lido não pode ser aplicado (ex.: cobrança cancelada,
// que o `start()` do backend recusa por construção): repetir o clique não muda nada.
const MOTIVO_NAO_ATUALIZAVEL = 'state_not_updatable'

function nomes(itens: { instance_name: string }[]): string {
  return itens.map((i) => i.instance_name).join(', ')
}

function plural(n: number, singular: string, pluralForm: string): string {
  return n === 1 ? singular : pluralForm
}

export function montarAlertaDeSincronizacao(
  resposta: RespostaDeSincronizacaoDeStatus | undefined,
): AlertaDeSincronizacao {
  const updates = resposta?.updates ?? []
  const unchecked = resposta?.unchecked ?? []
  const naoEncontradas = updates.filter((u) => u.reason === MOTIVO_NAO_ENCONTRADA)
  const atualizadas = updates.filter((u) => u.reason !== MOTIVO_NAO_ENCONTRADA)

  // Em `unchecked`, `vm_not_found` é a VM que o Proxmox não tem e ainda não foi julgada
  // (dentro do grace, ou em transição): tentar de novo em instantes não muda nada.
  const naoEncontradasSemVeredito = unchecked.filter((u) => u.reason === MOTIVO_NAO_ENCONTRADA)
  const naoAtualizaveis = unchecked.filter((u) => u.reason === MOTIVO_NAO_ATUALIZAVEL)
  const naoConferidas = unchecked.filter(
    (u) => u.reason !== MOTIVO_NAO_ENCONTRADA && u.reason !== MOTIVO_NAO_ATUALIZAVEL,
  )

  const linhas: string[] = []
  if (atualizadas.length > 0) {
    linhas.push(
      `${atualizadas.length} ${plural(atualizadas.length, 'VM teve', 'VMs tiveram')} o status atualizado com os dados mais recentes da infraestrutura.`,
    )
  }
  if (naoEncontradas.length > 0) {
    linhas.push(
      `${naoEncontradas.length} ${plural(naoEncontradas.length, 'VM não foi encontrada', 'VMs não foram encontradas')} na infraestrutura e ${plural(naoEncontradas.length, 'foi marcada', 'foram marcadas')} com erro: ${nomes(naoEncontradas)}. Entre em contato com o suporte.`,
    )
  }
  if (naoEncontradasSemVeredito.length > 0) {
    linhas.push(
      `${naoEncontradasSemVeredito.length} ${plural(naoEncontradasSemVeredito.length, 'VM não foi encontrada', 'VMs não foram encontradas')} na infraestrutura: ${nomes(naoEncontradasSemVeredito)}. A reconciliação automática vai confirmar o estado em alguns minutos.`,
    )
  }
  if (naoAtualizaveis.length > 0) {
    linhas.push(
      `${naoAtualizaveis.length} ${plural(naoAtualizaveis.length, 'VM está', 'VMs estão')} em um estado que não pode ser atualizado automaticamente: ${nomes(naoAtualizaveis)}.`,
    )
  }
  if (naoConferidas.length > 0) {
    linhas.push(
      `${naoConferidas.length} ${plural(naoConferidas.length, 'VM não pôde', 'VMs não puderam')} ser verificada${naoConferidas.length === 1 ? '' : 's'} agora: ${nomes(naoConferidas)}. Tente novamente em instantes.`,
    )
  }

  if (linhas.length === 0) {
    return {
      title: 'Status Sincronizado',
      message: 'Todos os status das instâncias já estavam corretos!',
      variant: 'info',
    }
  }

  const houveProblema = naoEncontradas.length > 0 || unchecked.length > 0
  return {
    title: houveProblema ? 'Sincronização com ressalvas' : 'Status Sincronizado',
    // `\n` funciona: o AlertModal renderiza a mensagem com `whitespace-pre-line`.
    message: linhas.join('\n\n'),
    variant: houveProblema ? 'warning' : 'success',
  }
}
